import { supabase } from '../lib/supabase';

/**
 * Production SMS Service
 * Implements Twilio-compliant architecture:
 * - Alert priority filtering (critical/high only)
 * - SMS rate limiting (1 per user per 5 minutes)
 * - Daily SMS cap (20 messages per user)
 * - Alert deduplication with cooldown timers
 * - SMS consent database tracking
 * - STOP and HELP command handling
 * - Twilio Messaging Service integration
 * - SMS logging with message SID, user ID, status
 * - Failover notifications (push) if SMS fails
 */

const SMS_PRIORITY_THRESHOLD = ['critical', 'high'];
const RATE_LIMIT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const DAILY_SMS_CAP = 20;
const DEFAULT_COOLDOWN_MINUTES = 30;

export const productionSMSService = {
  // ─────────────────────────────────────────────
  // PRIORITY FILTERING
  // ─────────────────────────────────────────────

  /**
   * Check if alert severity meets SMS threshold
   */
  meetsSmsPriority(severity) {
    return SMS_PRIORITY_THRESHOLD?.includes(severity?.toLowerCase());
  },

  // ─────────────────────────────────────────────
  // CONSENT MANAGEMENT
  // ─────────────────────────────────────────────

  /**
   * Check user SMS consent and opt-out status
   */
  async checkConsent(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('sms_consent, sms_opted_out, sms_opted_out_at')?.eq('id', userId)?.single();

      if (error || !data) return { consented: false, optedOut: false };

      return {
        consented: data?.sms_consent === true,
        optedOut: data?.sms_opted_out === true,
        optedOutAt: data?.sms_opted_out_at
      };
    } catch (err) {
      console.error('Consent check error:', err);
      return { consented: false, optedOut: false };
    }
  },

  /**
   * Record consent change in audit log
   */
  async logConsentChange(userId, phoneNumber, action, source = 'user', metadata = {}) {
    try {
      await supabase?.from('sms_consent_log')?.insert({
        user_id: userId,
        phone_number: phoneNumber,
        action,
        source,
        metadata
      });
    } catch (err) {
      console.error('Consent log error:', err);
    }
  },

  /**
   * Process STOP command - opt user out
   */
  async handleStopCommand(userId, phoneNumber) {
    try {
      await supabase?.from('user_profiles')?.update({
          sms_opted_out: true,
          sms_opted_out_at: new Date()?.toISOString(),
          sms_consent: false,
          sms_consent_updated_at: new Date()?.toISOString()
        })?.eq('id', userId);

      await this.logConsentChange(userId, phoneNumber, 'stop_command', 'user');

      return { success: true, message: 'You have been unsubscribed from Grow Guardian SMS alerts. Reply START to re-subscribe.' };
    } catch (err) {
      console.error('STOP command error:', err);
      throw err;
    }
  },

  /**
   * Process START command - opt user back in
   */
  async handleStartCommand(userId, phoneNumber) {
    try {
      await supabase?.from('user_profiles')?.update({
          sms_opted_out: false,
          sms_opted_out_at: null,
          sms_consent: true,
          sms_consent_updated_at: new Date()?.toISOString()
        })?.eq('id', userId);

      await this.logConsentChange(userId, phoneNumber, 'start_command', 'user');

      return { success: true, message: 'You have been re-subscribed to Grow Guardian SMS alerts.' };
    } catch (err) {
      console.error('START command error:', err);
      throw err;
    }
  },

  /**
   * Get HELP response text
   */
  getHelpResponse() {
    return 'Grow Guardian Alerts: Reply STOP to unsubscribe from all messages. Reply START to re-subscribe. For support: admin@growguardian.work';
  },

  // ─────────────────────────────────────────────
  // RATE LIMITING
  // ─────────────────────────────────────────────

  /**
   * Check if user is within rate limits
   * Returns { allowed: boolean, reason?: string }
   */
  async checkRateLimit(userId) {
    try {
      const now = new Date();
      const windowStart = new Date(now.getTime() - RATE_LIMIT_WINDOW_MS)?.toISOString();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())?.toISOString();

      // 5-minute window check
      const { data: recentSMS, error: recentErr } = await supabase?.from('sms_log')?.select('id')?.eq('user_id', userId)?.gte('created_at', windowStart)?.in('status', ['sent', 'queued']);

      if (!recentErr && recentSMS && recentSMS?.length >= 1) {
        return { allowed: false, reason: 'Rate limit: max 1 SMS per 5 minutes per user' };
      }

      // Daily cap check
      const { data: dailySMS, error: dailyErr } = await supabase?.from('sms_log')?.select('id')?.eq('user_id', userId)?.gte('created_at', todayStart)?.in('status', ['sent', 'queued']);

      if (!dailyErr && dailySMS && dailySMS?.length >= DAILY_SMS_CAP) {
        return { allowed: false, reason: `Daily SMS cap reached (${DAILY_SMS_CAP} messages/day)` };
      }

      return { allowed: true };
    } catch (err) {
      console.error('Rate limit check error:', err);
      return { allowed: true }; // Fail open for critical alerts
    }
  },

  // ─────────────────────────────────────────────
  // DEDUPLICATION
  // ─────────────────────────────────────────────

  /**
   * Generate deduplication key for an alert
   */
  generateDedupKey(userId, alertType, severity, resourceId = '') {
    return `${userId}_${alertType}_${severity}_${resourceId}`?.replace(/\s+/g, '_')?.toLowerCase();
  },

  /**
   * Check if alert is a duplicate within cooldown window
   */
  async isDuplicate(userId, dedupKey) {
    try {
      const { data, error } = await supabase?.from('sms_deduplication')?.select('id, expires_at')?.eq('user_id', userId)?.eq('dedup_key', dedupKey)?.gt('expires_at', new Date()?.toISOString())?.single();

      return !error && !!data;
    } catch (err) {
      return false;
    }
  },

  /**
   * Set deduplication record after sending
   */
  async setDedupRecord(userId, dedupKey, alertType, cooldownMinutes = DEFAULT_COOLDOWN_MINUTES) {
    try {
      const expiresAt = new Date(Date.now() + cooldownMinutes * 60 * 1000)?.toISOString();
      await supabase?.from('sms_deduplication')?.upsert(
        {
          user_id: userId,
          dedup_key: dedupKey,
          alert_type: alertType,
          last_sent_at: new Date()?.toISOString(),
          cooldown_minutes: cooldownMinutes,
          expires_at: expiresAt
        },
        { onConflict: 'user_id,dedup_key' }
      );
    } catch (err) {
      console.error('Dedup record error:', err);
    }
  },

  // ─────────────────────────────────────────────
  // SMS QUEUE (Redis-like queue in Supabase)
  // ─────────────────────────────────────────────

  /**
   * Add SMS to queue
   */
  async enqueue(queueItem) {
    try {
      const priorityMap = { critical: 1, high: 2, medium: 5, low: 8 };
      const { data, error } = await supabase?.from('sms_queue')?.insert({
          user_id: queueItem?.userId,
          phone_number: queueItem?.phoneNumber,
          message_body: queueItem?.message,
          alert_type: queueItem?.alertType,
          severity: queueItem?.severity,
          priority: priorityMap?.[queueItem?.severity] || 5,
          dedup_key: queueItem?.dedupKey,
          metadata: queueItem?.metadata || {}
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('SMS enqueue error:', err);
      throw err;
    }
  },

  /**
   * Get pending queue items (ordered by priority)
   */
  async getQueuedItems(limit = 10) {
    try {
      const { data, error } = await supabase?.from('sms_queue')?.select('*')?.eq('status', 'pending')?.lte('scheduled_at', new Date()?.toISOString())?.order('priority', { ascending: true })?.order('scheduled_at', { ascending: true })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Get queue error:', err);
      return [];
    }
  },

  // ─────────────────────────────────────────────
  // SMS LOGGING
  // ─────────────────────────────────────────────

  /**
   * Log SMS attempt
   */
  async logSMS(logData) {
    try {
      const { data, error } = await supabase?.from('sms_log')?.insert({
          user_id: logData?.userId,
          phone_number: logData?.phoneNumber,
          message_body: logData?.message,
          message_sid: logData?.messageSid || null,
          alert_type: logData?.alertType,
          severity: logData?.severity,
          status: logData?.status || 'queued',
          error_code: logData?.errorCode || null,
          error_message: logData?.errorMessage || null,
          failover_triggered: logData?.failoverTriggered || false,
          failover_channel: logData?.failoverChannel || null,
          dedup_key: logData?.dedupKey || null
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('SMS log error:', err);
      return null;
    }
  },

  /**
   * Update SMS log status
   */
  async updateSMSLog(logId, updates) {
    try {
      await supabase?.from('sms_log')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', logId);
    } catch (err) {
      console.error('SMS log update error:', err);
    }
  },

  /**
   * Get SMS logs for a user
   */
  async getSMSLogs(userId, options = {}) {
    try {
      let query = supabase?.from('sms_log')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (options?.limit) query = query?.limit(options?.limit);
      if (options?.status) query = query?.eq('status', options?.status);
      if (options?.from) query = query?.gte('created_at', options?.from);
      if (options?.to) query = query?.lte('created_at', options?.to);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Get SMS logs error:', err);
      return [];
    }
  },

  // ─────────────────────────────────────────────
  // FAILOVER NOTIFICATIONS
  // ─────────────────────────────────────────────

  /**
   * Send failover notification via push/in-app when SMS fails
   */
  async sendFailover(userId, alertData) {
    try {
      const { alertType, severity, title, message } = alertData;

      // Queue in-app/push notification as failover
      const { error } = await supabase?.from('notification_queue')?.insert({
        user_id: userId,
        title: `[Alert] ${title || alertType}`,
        message: message,
        notification_type: 'system_alert',
        priority: severity === 'critical' ? 'urgent' : 'high',
        metadata: {
          original_alert_type: alertType,
          severity,
          failover_reason: 'sms_delivery_failed',
          failover_channel: 'push'
        }
      });

      if (error) throw error;
      return { success: true, channel: 'push' };
    } catch (err) {
      console.error('Failover notification error:', err);
      return { success: false };
    }
  },

  // ─────────────────────────────────────────────
  // MAIN SEND METHOD (orchestrates all checks)
  // ─────────────────────────────────────────────

  /**
   * Send SMS alert with full production architecture
   * Runs: priority filter → consent → rate limit → dedup → send → log → failover
   */
  async sendAlert(params) {
    const {
      userId,
      phoneNumber,
      message,
      alertType = 'notification',
      severity = 'medium',
      title = '',
      resourceId = '',
      cooldownMinutes = DEFAULT_COOLDOWN_MINUTES,
      metadata = {}
    } = params;

    // 1. Priority filter
    if (!this.meetsSmsPriority(severity)) {
      return {
        sent: false,
        skipped: true,
        reason: `Severity '${severity}' below SMS threshold (critical/high only)`
      };
    }

    // 2. Consent check
    if (userId) {
      const consent = await this.checkConsent(userId);
      if (consent?.optedOut) {
        return { sent: false, skipped: true, reason: 'User opted out via STOP command' };
      }
      if (!consent?.consented) {
        return { sent: false, skipped: true, reason: 'User has not given SMS consent' };
      }
    }

    // 3. Rate limiting
    if (userId) {
      const rateCheck = await this.checkRateLimit(userId);
      if (!rateCheck?.allowed) {
        const logEntry = await this.logSMS({
          userId,
          phoneNumber,
          message,
          alertType,
          severity,
          status: 'blocked',
          errorMessage: rateCheck?.reason
        });
        return { sent: false, skipped: true, reason: rateCheck?.reason, logId: logEntry?.id };
      }
    }

    // 4. Deduplication
    const dedupKey = this.generateDedupKey(userId || phoneNumber, alertType, severity, resourceId);
    if (userId) {
      const duplicate = await this.isDuplicate(userId, dedupKey);
      if (duplicate) {
        return { sent: false, skipped: true, reason: 'Duplicate alert within cooldown window', dedupKey };
      }
    }

    // 5. Log initial attempt
    const logEntry = await this.logSMS({
      userId,
      phoneNumber,
      message,
      alertType,
      severity,
      status: 'queued',
      dedupKey
    });

    // 6. Send via Twilio edge function
    try {
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-twilio-sms`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            to: phoneNumber,
            message,
            alertType,
            severity,
            userId,
            dedupKey,
            cooldownMinutes
          })
        }
      );

      const result = await response?.json();

      if (result?.success) {
        // Update log with success
        if (logEntry?.id) {
          await this.updateSMSLog(logEntry?.id, {
            message_sid: result?.messageSid,
            status: 'sent'
          });
        }

        // Set dedup record
        if (userId) {
          await this.setDedupRecord(userId, dedupKey, alertType, cooldownMinutes);
        }

        return {
          sent: true,
          messageSid: result?.messageSid,
          logId: logEntry?.id,
          dedupKey
        };
      } else if (result?.skipped) {
        // Skipped by edge function (consent/rate limit checked server-side too)
        if (logEntry?.id) {
          await this.updateSMSLog(logEntry?.id, { status: 'skipped', error_message: result?.reason });
        }
        return { sent: false, skipped: true, reason: result?.reason };
      } else {
        // SMS failed - trigger failover
        if (logEntry?.id) {
          await this.updateSMSLog(logEntry?.id, {
            status: 'failed',
            error_code: result?.errorCode || null,
            error_message: result?.error || 'Unknown error'
          });
        }

        let failoverResult = { success: false };
        if (userId) {
          failoverResult = await this.sendFailover(userId, { alertType, severity, title, message });
          if (logEntry?.id && failoverResult?.success) {
            await this.updateSMSLog(logEntry?.id, {
              failover_triggered: true,
              failover_channel: failoverResult?.channel || 'push'
            });
          }
        }

        return {
          sent: false,
          error: result?.error,
          errorCode: result?.errorCode,
          failoverTriggered: failoverResult?.success,
          failoverChannel: failoverResult?.channel,
          logId: logEntry?.id
        };
      }
    } catch (err) {
      console.error('SMS send error:', err);

      if (logEntry?.id) {
        await this.updateSMSLog(logEntry?.id, {
          status: 'failed',
          error_message: err?.message
        });
      }

      // Failover on exception
      if (userId) {
        await this.sendFailover(userId, { alertType, severity, title, message });
      }

      return { sent: false, error: err?.message, logId: logEntry?.id };
    }
  },

  // ─────────────────────────────────────────────
  // BATCH SEND (for multiple recipients)
  // ─────────────────────────────────────────────

  /**
   * Send SMS alerts to multiple users
   */
  async sendBatchAlerts(recipientUserIds, alertParams) {
    const results = [];

    for (const userId of recipientUserIds) {
      try {
        // Get user phone number and preferences
        const { data: profile } = await supabase?.from('user_profiles')?.select('phone_number, sms_consent, sms_opted_out')?.eq('id', userId)?.single();

        if (!profile?.phone_number) {
          results?.push({ userId, sent: false, reason: 'No phone number' });
          continue;
        }

        const result = await this.sendAlert({
          ...alertParams,
          userId,
          phoneNumber: profile?.phone_number
        });

        results?.push({ userId, ...result });
      } catch (err) {
        console.error(`Batch SMS error for user ${userId}:`, err);
        results?.push({ userId, sent: false, error: err?.message });
      }
    }

    return {
      totalRecipients: recipientUserIds?.length,
      sent: results?.filter(r => r?.sent)?.length,
      skipped: results?.filter(r => r?.skipped)?.length,
      failed: results?.filter(r => !r?.sent && !r?.skipped)?.length,
      results
    };
  },

  // ─────────────────────────────────────────────
  // STATS & REPORTING
  // ─────────────────────────────────────────────

  /**
   * Get SMS delivery statistics
   */
  async getSMSStats(userId, dateRange = {}) {
    try {
      let query = supabase?.from('sms_log')?.select('status, severity, alert_type, created_at');

      if (userId) query = query?.eq('user_id', userId);
      if (dateRange?.from) query = query?.gte('created_at', dateRange?.from);
      if (dateRange?.to) query = query?.lte('created_at', dateRange?.to);

      const { data, error } = await query;
      if (error) throw error;

      const logs = data || [];
      return {
        total: logs?.length,
        sent: logs?.filter(l => l?.status === 'sent')?.length,
        failed: logs?.filter(l => l?.status === 'failed')?.length,
        blocked: logs?.filter(l => l?.status === 'blocked')?.length,
        skipped: logs?.filter(l => l?.status === 'skipped')?.length,
        failoverTriggered: logs?.filter(l => l?.failover_triggered)?.length
      };
    } catch (err) {
      console.error('SMS stats error:', err);
      return { total: 0, sent: 0, failed: 0, blocked: 0, skipped: 0 };
    }
  }
};

export default productionSMSService;
